import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { fileUrl, API_BASE } from "../../lib/api";
import {
  ArrowLeft, Upload, Calendar, MapPin, Trash2, Eye, EyeOff, ExternalLink,
  Image as ImageIcon, AlertCircle, QrCode, Copy, Download as DownloadIcon,
  Megaphone, ImagePlus, BarChart3, ScanLine, PlayCircle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
}

export default function AdminEventEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("details");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState([]);
  const [assetVer, setAssetVer] = useState(0); // cache-buster for banner/strip preview
  const fileRef = useRef(null);
  const dropRef = useRef(null);

  const load = async () => {
    const [ev, ph] = await Promise.all([
      api.get(`/admin/events/${id}`),
      api.get(`/admin/events/${id}/photos`),
    ]);
    setEvent(ev.data);
    setForm(ev.data);
    setPhotos(ph.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Failed to load event"));
    // eslint-disable-next-line
  }, [id]);

  const save = async () => {
    try {
      const res = await api.put(`/admin/events/${id}`, {
        name: form.name,
        description: form.description,
        banner_url: form.banner_url || "",
        country: form.country,
        city: form.city,
        organizer_name: form.organizer_name,
        event_date: form.event_date,
        status: form.status,
      });
      setEvent(res.data);
      toast.success("Event updated");
    } catch {
      toast.error("Save failed");
    }
  };

  const togglePublish = async () => {
    const endpoint = event.status === "published" ? "unpublish" : "publish";
    await api.post(`/admin/events/${id}/${endpoint}`);
    toast.success(`Event ${endpoint}ed`);
    load();
  };

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setUploading(true);
    setProgress(0);
    setErrors([]);
    try {
      const res = await api.post(`/admin/events/${id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
        timeout: 600000,
      });
      toast.success(`Uploaded ${res.data.count} photos. OCR running.`);
      if (res.data.errors?.length) setErrors(res.data.errors);
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("drag-active");
    handleFiles(e.dataTransfer.files);
  };

  const deletePhoto = async (pid) => {
    if (!window.confirm("Delete this photo?")) return;
    await api.delete(`/admin/photos/${pid}`);
    setPhotos((arr) => arr.filter((p) => p.id !== pid));
    toast.success("Photo deleted");
  };

  const deleteEvent = async () => {
    if (!window.confirm(`Delete "${event.name}"? This removes all photos permanently.`)) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Event deleted");
    navigate("/admin/events");
  };

  const uploadAsset = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/events/${id}/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      toast.success(`${kind === "banner" ? "Cover image" : "Advert strip"} uploaded`);
      setAssetVer((v) => v + 1);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    }
  };

  const removeAsset = async (kind) => {
    const nice = kind === "banner" ? "cover image" : "advert strip";
    if (!window.confirm(`Remove ${nice}?`)) return;
    await api.delete(`/admin/events/${id}/${kind}`);
    toast.success(`${nice} removed`);
    setAssetVer((v) => v + 1);
    load();
  };

  if (!event || !form) {
    return <div className="px-4 sm:px-6 lg:px-10 py-12 max-w-7xl"><p className="font-mono text-sm text-[#475569]">LOADING…</p></div>;
  }

  const publicUrl = `${window.location.origin}/events/${event.slug}`;
  const qrUrl = `${BACKEND_URL}/api/public/events/${event.slug}/qr`;

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 md:py-12 max-w-7xl">
      <Link to="/admin/events" className="text-xs text-[#475569] hover:text-[#0F172A] inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={12} /> ALL EVENTS
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div className="min-w-0">
          <p className="kicker mb-2">{event.city?.toUpperCase()} · {event.country?.toUpperCase()}</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tighter truncate" data-testid="event-editor-title">{event.name}</h1>
          <div className="mt-3 flex items-center gap-4 text-xs text-[#475569] flex-wrap">
            <span className="inline-flex items-center gap-1.5"><Calendar size={12} />{event.event_date}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin size={12} />{event.city}, {event.country}</span>
            <span className="inline-flex items-center gap-1.5"><ImageIcon size={12} />{photos.length} photos</span>
            <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase border ${
              event.status === "published" ? "bg-[#10B981] text-white border-[#10B981]" : "bg-[#F8FAFC] text-[#475569] border-[#E2E8F0]"
            }`}>{event.status}</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to={`/admin/events/${id}/analytics`}
            className="text-sm font-semibold inline-flex items-center gap-2 border border-[#E2E8F0] hover:border-[#5B4CFF] text-[#0F172A] px-4 py-2.5 transition-colors"
            data-testid="event-analytics-link"
          >
            <BarChart3 size={14} /> Analytics
          </Link>
          <button onClick={togglePublish} className="text-sm font-semibold inline-flex items-center gap-2 border border-[#E2E8F0] hover:border-[#5B4CFF] px-4 py-2.5 transition-colors" data-testid="event-toggle-publish">
            {event.status === "published" ? <><EyeOff size={14} /> Unpublish</> : <><Eye size={14} /> Publish</>}
          </button>
          {event.status === "published" && (
            <a href={`/events/${event.slug}`} target="_blank" rel="noreferrer" className="text-sm font-semibold inline-flex items-center gap-2 border border-[#E2E8F0] hover:border-[#5B4CFF] px-4 py-2.5 transition-colors">
              <ExternalLink size={14} /> View public
            </a>
          )}
          <button onClick={deleteEvent} className="text-sm font-semibold inline-flex items-center gap-2 bg-[#0F172A] hover:bg-black text-white px-4 py-2.5 transition-colors" data-testid="event-delete-btn">
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#E2E8F0] mb-8 overflow-x-auto">
        {[
          { id: "details", label: "Details" },
          { id: "assets", label: "Cover, Advert & QR" },
          { id: "photos", label: `Photos (${photos.length})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
              tab === t.id ? "text-[#5B4CFF] border-[#5B4CFF]" : "text-[#475569] border-transparent hover:text-[#0F172A]"
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "details" && (
        <div className="bg-white border border-[#E2E8F0] p-6 md:p-8 max-w-3xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} dt="ed-name" full />
            <DField label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} dt="ed-country" />
            <DField label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} dt="ed-city" />
            <DField label="Event date" type="date" value={form.event_date} onChange={(v) => setForm({ ...form, event_date: v })} dt="ed-date" />
            <DField label="Organizer name" value={form.organizer_name} onChange={(v) => setForm({ ...form, organizer_name: v })} dt="ed-organizer" />
            <div className="md:col-span-2">
              <label className="block text-xs font-mono tracking-widest text-[#475569] mb-2">DESCRIPTION</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                className="w-full bg-white border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
                data-testid="ed-description"
              />
            </div>
            <p className="md:col-span-2 text-xs text-[#475569]">
              Upload the event cover image, sponsor advert strip and download the QR code in the <b>Cover, Advert &amp; QR</b> tab.
            </p>
          </div>
          <div className="mt-6 flex justify-end">
            <button onClick={save} className="bg-[#5B4CFF] hover:bg-[#4a3dde] text-white font-semibold px-6 py-2.5 text-sm" data-testid="ed-save-btn">
              Save changes
            </button>
          </div>
        </div>
      )}

      {tab === "assets" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AssetCard
            title="Event cover image"
            hint="Shown on the landing page grid and in social share previews. Recommended: 1600 × 900 px (16:9). JPG, PNG or WEBP. Max 5 MB."
            icon={ImagePlus}
            currentUrl={event.banner_url ? `${event.banner_url}?v=${assetVer}` : ""}
            aspect="aspect-[16/9]"
            onSelect={(f) => uploadAsset("banner", f)}
            onRemove={() => removeAsset("banner")}
            testid="banner"
          />
          <AssetCard
            title="Sponsor advert strip"
            hint="Auto-composited under every photo download and social preview. Recommended: 1200 × 200 px (6:1 ratio, thin bar). JPG, PNG or WEBP. Max 2 MB."
            icon={Megaphone}
            currentUrl={event.advert_strip_url ? `${event.advert_strip_url}?v=${assetVer}` : ""}
            aspect="aspect-[6/1]"
            onSelect={(f) => uploadAsset("advert-strip", f)}
            onRemove={() => removeAsset("advert-strip")}
            testid="advert-strip"
          />

          <div className="lg:col-span-2 bg-white border border-[#E2E8F0] p-6 md:p-8" data-testid="qr-panel">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
              <div>
                <p className="kicker mb-2 inline-flex items-center gap-2"><QrCode size={14} /> ATTENDEE ACCESS</p>
                <h3 className="font-display text-xl font-bold text-[#0F172A]">QR code &amp; public link</h3>
                <p className="mt-2 text-sm text-[#475569] max-w-xl">
                  Print or share this QR at your race village. Anyone who scans lands directly on the event&apos;s photo-search page.
                </p>
              </div>
              {event.status !== "published" && (
                <span className="text-xs font-semibold inline-flex items-center gap-2 bg-[#F8FAFC] text-[#475569] border border-[#E2E8F0] px-3 py-1.5">
                  <AlertCircle size={12} /> Publish the event to activate
                </span>
              )}
            </div>

            {event.status === "published" ? (
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="w-40 h-40 md:w-48 md:h-48 border border-[#E2E8F0] p-2 bg-white shrink-0" data-testid="qr-preview">
                  <img src={qrUrl} alt="Event QR" className="w-full h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0 space-y-4">
                  <div>
                    <p className="text-[10px] font-mono tracking-widest text-[#475569] mb-2">PUBLIC LINK</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        readOnly
                        value={publicUrl}
                        onFocus={(e) => e.target.select()}
                        className="flex-1 bg-[#F8FAFC] border border-[#E2E8F0] px-3 py-2.5 text-xs font-mono focus:outline-none"
                        data-testid="qr-public-link"
                      />
                      <button
                        onClick={async () => (await copyText(publicUrl)) && toast.success("Link copied")}
                        className="inline-flex items-center gap-2 border border-[#E2E8F0] hover:border-[#5B4CFF] px-3 py-2.5 text-xs font-semibold text-[#0F172A]"
                        data-testid="qr-copy-link"
                      >
                        <Copy size={12} /> Copy
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={qrUrl}
                      download={`racesnap-${event.slug}-qr.png`}
                      className="inline-flex items-center gap-2 bg-[#5B4CFF] hover:bg-[#4a3dde] text-white font-semibold px-4 py-2.5 text-sm transition-colors"
                      data-testid="qr-download"
                    >
                      <DownloadIcon size={14} /> Download QR (PNG)
                    </a>
                    <a
                      href={`/events/${event.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 border border-[#E2E8F0] hover:border-[#5B4CFF] text-[#0F172A] font-semibold px-4 py-2.5 text-sm transition-colors"
                      data-testid="qr-open"
                    >
                      <ExternalLink size={14} /> Open public page
                    </a>
                  </div>
                  <p className="text-[11px] text-[#475569]">
                    Print at least 5×5 cm at 300 dpi for reliable phone scanning.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#475569]">The QR code and public link become active once the event is published.</p>
            )}
          </div>
        </div>
      )}

      {tab === "photos" && (
        <>
          <OcrReindexPanel eventId={id} photoCount={photos.length} onDone={load} />

          <div
            ref={dropRef}
            onDragOver={(e) => { e.preventDefault(); dropRef.current?.classList.add("drag-active"); }}
            onDragLeave={() => dropRef.current?.classList.remove("drag-active")}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            className="dropzone cursor-pointer p-10 md:p-14 text-center bg-white mb-6"
            data-testid="photo-dropzone"
          >
            <Upload size={28} className="mx-auto text-[#5B4CFF] mb-4" />
            <p className="font-display text-lg font-semibold text-[#0F172A]">
              {uploading ? `Uploading… ${progress}%` : "Drop photos here or click to upload"}
            </p>
            <p className="mt-1 text-sm text-[#475569]">JPG, PNG, WEBP · Bib numbers detected automatically</p>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="photo-file-input"
            />
            {uploading && (
              <div className="mt-4 max-w-md mx-auto bg-[#F8FAFC] border border-[#E2E8F0] h-2 overflow-hidden">
                <div className="h-full bg-[#5B4CFF]" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>

          {errors.length > 0 && (
            <div className="mb-6 border border-[#EF4444]/40 bg-[#fef2f2] p-4 text-xs text-[#0F172A]" data-testid="upload-errors">
              <p className="font-semibold mb-2 inline-flex items-center gap-2"><AlertCircle size={14} className="text-[#EF4444]" /> Some files failed:</p>
              <ul className="list-disc pl-5 space-y-1">
                {errors.map((e, i) => <li key={i}>{e.filename}: {e.error}</li>)}
              </ul>
            </div>
          )}

          {photos.length === 0 ? (
            <div className="border border-dashed border-[#E2E8F0] p-12 text-center bg-white">
              <p className="text-sm text-[#475569]">No photos uploaded yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="photo-grid">
              {photos.map((p) => (
                <div key={p.id} className="group relative aspect-square border border-[#E2E8F0] bg-[#F8FAFC] overflow-hidden">
                  <img src={fileUrl(p.id)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-end p-2">
                    <div className="w-full flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex flex-wrap gap-1">
                        {p.bib_numbers?.length ? p.bib_numbers.slice(0, 3).map((b) => (
                          <span key={b} className="bg-[#5B4CFF] text-white text-[10px] font-mono px-1.5 py-0.5">#{b}</span>
                        )) : (
                          <span className="bg-white/20 text-white text-[10px] font-mono px-1.5 py-0.5">no bib</span>
                        )}
                      </div>
                      <button onClick={() => deletePhoto(p.id)} className="text-white p-1 hover:bg-white/20" data-testid={`del-photo-${p.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DField({ label, value, onChange, dt, full, type = "text" }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-xs font-mono tracking-widest text-[#475569] mb-2">{label.toUpperCase()}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
        data-testid={dt}
      />
    </div>
  );
}

function AssetCard({ title, hint, icon: Icon, currentUrl, aspect, onSelect, onRemove, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const openPicker = () => inputRef.current?.click();

  const onChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { await onSelect(f); } finally { setBusy(false); e.target.value = ""; }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    setBusy(true);
    try { await onSelect(f); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] p-6" data-testid={`asset-${testid}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-[#5B4CFF] text-white flex items-center justify-center shrink-0">
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">{title}</h3>
          <p className="mt-1 text-xs text-[#475569] leading-relaxed">{hint}</p>
        </div>
      </div>

      <div
        onClick={openPicker}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative cursor-pointer border-2 border-dashed ${aspect} transition-colors overflow-hidden ${
          dragOver ? "border-[#5B4CFF] bg-[#f3f1ff]" : currentUrl ? "border-[#E2E8F0]" : "border-[#E2E8F0] bg-[#F8FAFC] hover:border-[#5B4CFF]"
        }`}
        data-testid={`asset-drop-${testid}`}
      >
        {currentUrl ? (
          <img src={currentUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-[#475569] p-4">
            <Upload size={20} className="text-[#5B4CFF] mb-2" />
            <p className="text-sm font-semibold text-[#0F172A]">
              {busy ? "Uploading…" : "Click or drop image"}
            </p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={onChange}
          data-testid={`asset-input-${testid}`}
        />
      </div>

      <div className="mt-4 flex justify-end gap-2">
        {currentUrl && (
          <button
            onClick={onRemove}
            className="text-xs font-semibold inline-flex items-center gap-1.5 border border-[#E2E8F0] hover:border-[#EF4444] hover:text-[#EF4444] px-3 py-2 transition-colors"
            data-testid={`asset-remove-${testid}`}
          >
            <Trash2 size={12} /> Remove
          </button>
        )}
        <button
          onClick={openPicker}
          className="text-xs font-semibold inline-flex items-center gap-1.5 bg-[#5B4CFF] hover:bg-[#4a3dde] text-white px-3 py-2 transition-colors"
          data-testid={`asset-replace-${testid}`}
        >
          <Upload size={12} /> {currentUrl ? "Replace" : "Upload"}
        </button>
      </div>
    </div>
  );
}


function OcrReindexPanel({ eventId, photoCount, onDone }) {
  const [preview, setPreview] = React.useState(null);
  const [job, setJob] = React.useState(null);
  const [starting, setStarting] = React.useState(false);
  const pollRef = React.useRef(null);

  const refreshPreview = React.useCallback(() => {
    api.get(`/admin/events/${eventId}/ocr/preview`).then((r) => setPreview(r.data)).catch(() => {});
  }, [eventId]);

  const refreshJob = React.useCallback(() => {
    api.get(`/admin/ocr/status`).then((r) => setJob(r.data)).catch(() => {});
  }, []);

  React.useEffect(() => {
    refreshPreview();
    refreshJob();
  }, [refreshPreview, refreshJob, photoCount]);

  // Poll while running
  React.useEffect(() => {
    if (job?.state === "running") {
      pollRef.current = setInterval(() => refreshJob(), 3000);
      return () => clearInterval(pollRef.current);
    }
    // On completion, refresh preview + photos so admin sees new mappings
    if (job?.state === "completed" && job?.event_id === eventId && job?.total > 0) {
      refreshPreview();
      onDone?.();
    }
    // eslint-disable-next-line
  }, [job?.state, job?.event_id]);

  const start = async () => {
    setStarting(true);
    try {
      const r = await api.post(`/admin/events/${eventId}/ocr/reindex`);
      setJob({ ...r.data.job, remaining: r.data.job.total });
      if (r.data.job.state === "completed") toast.success("Nothing to re-index — all photos already have bib mappings");
      else toast.success(`Re-indexing ${r.data.job.total} photo${r.data.job.total === 1 ? "" : "s"} in background`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to start job");
    } finally {
      setStarting(false);
    }
  };

  if (!preview) return null;

  const jobForThisEvent = job && job.event_id === eventId;
  const running = jobForThisEvent && job.state === "running";
  const showResults = jobForThisEvent && (job.state === "completed" || job.state === "error") && job.total > 0;
  const remaining = jobForThisEvent ? Math.max(0, job.total - job.processed) : preview.missing_ocr;
  const pct = jobForThisEvent && job.total ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <div className="mb-6 bg-white border border-[#E2E8F0] p-5 md:p-6" data-testid="ocr-reindex-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#5B4CFF] text-white flex items-center justify-center shrink-0">
            <ScanLine size={18} />
          </div>
          <div className="min-w-0">
            <p className="kicker mb-1">OCR RE-INDEXING</p>
            <h3 className="font-display text-lg font-semibold text-[#0F172A]">Run OCR for missing photos</h3>
            <p className="mt-1 text-xs text-[#475569] max-w-xl">
              Detects bib numbers on any photo that isn&apos;t yet mapped. Runs in the background — you can close this page.
              Photos previously scanned with no bib are skipped.
            </p>
          </div>
        </div>
        <button
          onClick={start}
          disabled={starting || running || preview.missing_ocr === 0}
          className="text-sm font-semibold inline-flex items-center gap-2 bg-[#5B4CFF] hover:bg-[#4a3dde] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2.5 transition-colors"
          data-testid="ocr-run-btn"
        >
          {running ? <><ScanLine size={14} className="animate-pulse" /> Running…</> :
           starting ? "Starting…" :
           preview.missing_ocr === 0 ? <><CheckCircle2 size={14} /> Nothing to re-index</> :
           <><PlayCircle size={14} /> Run OCR ({preview.missing_ocr})</>}
        </button>
      </div>

      {/* Snapshot counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <MiniStat label="Total photos" value={preview.total_photos} />
        <MiniStat label="Missing OCR" value={preview.missing_ocr} accent={preview.missing_ocr > 0} />
        <MiniStat label="Already indexed" value={preview.already_indexed} />
        <MiniStat label="No bib detected" value={preview.already_no_bib} />
      </div>

      {(running || showResults) && (
        <div className="border-t border-[#E2E8F0] pt-4" data-testid="ocr-progress">
          <div className="flex items-center justify-between mb-2">
            <p className="kicker">{running ? "PROCESSING" : "LAST RUN"}</p>
            <p className="text-xs font-mono text-[#475569]">
              {job.processed} / {job.total}{job.total ? ` · ${pct}%` : ""}
            </p>
          </div>
          <div className="w-full h-2 bg-[#F8FAFC] border border-[#E2E8F0] overflow-hidden">
            <div className="h-full bg-[#5B4CFF] transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniStat label="Indexed" value={job.indexed} good />
            <MiniStat label="No bib" value={job.no_bib} />
            <MiniStat label="Failed" value={job.failed} warn={job.failed > 0} />
            <MiniStat label="Remaining" value={remaining} />
          </div>
          {job.last_error && (
            <p className="mt-3 text-xs text-[#EF4444] font-mono" data-testid="ocr-error-msg">Error: {job.last_error}</p>
          )}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, good, warn, accent }) {
  const cls = good ? "text-[#10B981]" : warn ? "text-[#EF4444]" : accent ? "text-[#5B4CFF]" : "text-[#0F172A]";
  return (
    <div className="border border-[#E2E8F0] p-3 bg-white">
      <p className="text-[10px] font-mono tracking-widest text-[#475569]">{label.toUpperCase()}</p>
      <p className={`mt-1 font-display text-2xl font-extrabold ${cls}`}>{value}</p>
    </div>
  );
}
